/**
 * ProfilePage - User Profile & Dashboard
 *
 * Migrated to BasePage architecture.
 * Demonstrates: User info display, action cards, conditional sections, role badges, developer tools.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Search,
  Heart,
  Bell,
  Settings,
  Code,
  Copy,
  Check,
  Eye,
  EyeOff,
  ExternalLink,
  List,
  Plus,
  Building2,
} from "lucide-react";

// Hooks & Contexts
import {
  useTranslation,
  useSnackbar,
  useDeduplicatedEffect,
} from "@/shared/hooks";
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";

// New Design System
// New Design System
import { BasePage } from "@/shared/ui/layout";
import { Box, Text } from "@/shared/ui/base";
import { Card, Button } from "@/shared/ui/components";

// Components
import {
  VerificationBadge,
  VerificationModal,
} from "@/features/verification/components";

// API
import { getProfile, type Profile } from "@/shared/api/profileApi";
import {
  getVerificationStatus,
  type VerificationStatus,
} from "@/shared/api/verificationApi";

// Config
import { env } from "@/shared/config/env";

// Constants
import { StorageKeys, ROUTES } from "@/shared/constants";

/**
 * Role badge color mapping
 */
function getRoleBadgeClasses(role: string): string {
  switch (role) {
    case "admin":
      return "bg-green-100 text-green-800";
    case "developer":
      return "bg-blue-100 text-blue-800";
    case "partner":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

/**
 * Check if user has developer-level access
 */
function hasDevAccess(globalRoles: string[] | undefined): boolean {
  return (
    globalRoles?.some((role) => role === "admin" || role === "developer") ??
    false
  );
}

/**
 * Check if user is a partner
 */
function isPartner(
  authMe: { orgs?: Array<{ role: string }> } | null | undefined,
): boolean {
  return authMe?.orgs?.some((org) => org.role?.startsWith("partner_")) ?? false;
}

/**
 * Get display name from user data
 */
function getDisplayName(
  user: { displayName?: string | null; email?: string | null } | null,
): string {
  if (!user) return "User";
  return user.displayName || user.email?.split("@")[0] || "User";
}

/**
 * Get organization name or "Individual" for partners
 */
function getOrgDisplayName(
  authMe: { orgs?: Array<{ org_id: string }> } | null | undefined,
): string | null {
  const firstOrg = authMe?.orgs?.[0];
  if (!firstOrg) return null;
  const org_id = firstOrg.org_id;
  // Individual partners have org IDs starting with "individual-"
  if (org_id?.startsWith("individual-")) {
    return "Individual Partner";
  }
  return org_id; // In a real app, we'd fetch the org name
}

export const ProfilePage = observer(function ProfilePage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { authStore } = useStore();
  const { user, getIdToken, authMe } = authStore;

  // State
  const [token, setToken] = useState<string>("");
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus>("unverified");
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Derived state - use profile data when available for proper names
  const displayName = useMemo(() => {
    // Priority: profile.onboarding names > user.displayName > email
    if (profile?.onboarding?.first_name && profile?.onboarding?.last_name) {
      return `${profile.onboarding.first_name} ${profile.onboarding.last_name}`;
    }
    return getDisplayName(user);
  }, [profile, user]);

  const showDevTools = useMemo(
    () => hasDevAccess(authMe?.global_roles),
    [authMe?.global_roles],
  );
  const userIsPartner = useMemo(() => isPartner(authMe), [authMe]);

  // Get user-friendly org name
  const orgDisplayName = useMemo(() => {
    // Check if individual partner from profile
    if (profile?.onboarding?.is_individual) {
      return t("profileIndividual");
    }
    // Get org name from profile.orgs (has the actual name)
    const firstOrg = profile?.orgs?.[0];
    if (firstOrg?.name && !firstOrg.name.startsWith("individual-")) {
      return firstOrg.name;
    }
    // Fallback to old method
    return getOrgDisplayName(authMe);
  }, [profile, authMe, t]);

  /**
   * Fetch profile data from API for proper names
   * Uses deduplicated effect to prevent duplicate calls in React Strict Mode
   */
  useDeduplicatedEffect(() => {
    let cancelled = false;

    const fetchProfile = async () => {
      const idToken = await getIdToken();
      if (!idToken || cancelled) return;

      try {
        const profileData = await getProfile(idToken);
        if (!cancelled) {
          setProfile(profileData);
        }
      } catch (err) {
        // Silent fail - fallback to Firebase user data
        console.debug("Failed to fetch profile:", err);
      }
    };
    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [getIdToken]);

  /**
   * Show welcome message only once per session
   */
  useEffect(() => {
    if (!user || !authMe) return;

    const welcomeKey = StorageKeys.dashboardWelcomeShown;
    try {
      if (sessionStorage.getItem(welcomeKey) === "1") return;
      sessionStorage.setItem(welcomeKey, "1");
    } catch {
      return;
    }

    const isAdmin = authMe.global_roles?.includes("admin") ?? false;
    if (isAdmin) {
      showSnackbar(t("welcomeAdminSnackbar"), "success", 5000);
    } else {
      showSnackbar(t("welcomeUserSnackbar"), "info", 5000);
    }
  }, [user, authMe, showSnackbar, t]);

  /**
   * Fetch verification status for partners
   */
  useEffect(() => {
    if (!userIsPartner) return;

    const fetchVerification = async () => {
      const idToken = await getIdToken();
      if (!idToken) return;

      try {
        const status = await getVerificationStatus(idToken);
        setVerificationStatus(status.status);
      } catch (err) {
        console.debug("Failed to fetch verification status:", err);
      }
    };
    fetchVerification();
  }, [userIsPartner, getIdToken]);

  /**
   * Fetch ID token for developer tools
   */
  useEffect(() => {
    const fetchToken = async () => {
      const idToken = await getIdToken();
      if (idToken) {
        setToken(idToken);
      }
    };
    fetchToken();
  }, [getIdToken]);

  const handleCopyToken = useCallback(async () => {
    if (token) {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [token]);

  const handleToggleToken = useCallback(() => {
    setShowToken((prev) => !prev);
  }, []);

  const handleSearchClick = useCallback(() => {
    navigate(ROUTES.OVERVIEW);
  }, [navigate]);

  const handleCreateListingClick = useCallback(() => {
    navigate(ROUTES.CREATE_LISTING);
  }, [navigate]);

  const handleMyListingsClick = useCallback(() => {
    navigate(ROUTES.MY_LISTINGS);
  }, [navigate]);

  const handleSettingsClick = useCallback(() => {
    navigate(ROUTES.SETTINGS);
  }, [navigate]);

  return (
    <BasePage
      title={t("profileWelcome")}
      description="User profile and dashboard"
      maxWidth="xl"
      background="gray"
      headerVariant="scroll-aware"
    >
      {/* Welcome Header */}
      <Box className="mb-8">
        <Text variant="h1" className="mb-2">
          {t("profileWelcome")}, {displayName}!
        </Text>
        <Text variant="body" color="secondary">
          {t("profileSubtitle")}
        </Text>
      </Box>

      {/* User Info Card */}
      <Card variant="default" padding="md" className="shadow-lg mb-6">
        <Box className="flex items-center gap-4">
          <Box className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-red-600" />
          </Box>
          <Box className="flex-1">
            <Box className="flex items-center gap-2">
              <Text variant="h4" className="text-gray-900">
                {displayName}
              </Text>
              {/* Verification Badge for Partners */}
              {userIsPartner && (
                <VerificationBadge status={verificationStatus} />
              )}
            </Box>
            <Text variant="body" color="secondary">
              {user?.email}
            </Text>
            {/* Partner org info */}
            {userIsPartner && orgDisplayName && (
              <Box className="flex items-center gap-2 mt-1">
                <Building2 className="w-4 h-4 text-gray-400" />
                <Text variant="body" className="text-sm text-gray-600">
                  {orgDisplayName}
                </Text>
              </Box>
            )}
            {/* Role badges */}
            {authMe && authMe.global_roles && (
              <Box className="flex gap-2 mt-2 flex-wrap">
                {authMe.global_roles.map((role: string) => (
                  <Text
                    key={role}
                    as="span"
                    variant="caption"
                    className={`px-2 py-0.5 font-medium rounded-full ${getRoleBadgeClasses(role)}`}
                  >
                    {role}
                  </Text>
                ))}
                {userIsPartner && (
                  <Text
                    as="span"
                    variant="caption"
                    className="px-2 py-0.5 font-medium rounded-full bg-purple-100 text-purple-800"
                  >
                    partner
                  </Text>
                )}
              </Box>
            )}
          </Box>
          {/* Create listing button for partners */}
          {userIsPartner && (
            <Button variant="primary" onClick={handleCreateListingClick}>
              <Plus className="w-5 h-5 mr-1" />
              {t("profileCreateListing")}
            </Button>
          )}
        </Box>
      </Card>

      {/* Quick Actions Grid */}
      <Box
        className={`grid grid-cols-1 md:grid-cols-2 ${userIsPartner ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-4 mb-6`}
      >
        {/* Search */}
        <Card
          variant="hover"
          padding="md"
          onClick={handleSearchClick}
          className="cursor-pointer"
        >
          <Box className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-3">
            <Search className="w-6 h-6 text-red-600" />
          </Box>
          <Text variant="h5" className="mb-1">
            {t("profileSearch")}
          </Text>
          <Text variant="body" className="text-sm text-gray-500">
            {t("profileSearchDesc")}
          </Text>
        </Card>

        {/* My Listings - Partners only */}
        {userIsPartner && (
          <Card
            variant="hover"
            padding="md"
            onClick={handleMyListingsClick}
            className="cursor-pointer"
          >
            <Box className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
              <List className="w-6 h-6 text-purple-600" />
            </Box>
            <Text variant="h5" className="mb-1">
              {t("profileMyListings")}
            </Text>
            <Text variant="body" className="text-sm text-gray-500">
              {t("profileMyListingsDesc")}
            </Text>
          </Card>
        )}

        {/* Favorites */}
        <Card
          variant="hover"
          padding="md"
          className="cursor-pointer opacity-60"
        >
          <Box className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-3">
            <Heart className="w-6 h-6 text-pink-600" />
          </Box>
          <Text variant="h5" className="mb-1">
            {t("profileFavorites")}
          </Text>
          <Text variant="body" className="text-sm text-gray-500">
            {t("profileFavoritesDesc")}
          </Text>
        </Card>

        {/* Notifications */}
        <Card
          variant="hover"
          padding="md"
          className="cursor-pointer opacity-60"
        >
          <Box className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
            <Bell className="w-6 h-6 text-amber-600" />
          </Box>
          <Text variant="h5" className="mb-1">
            {t("profileNotifications")}
          </Text>
          <Text variant="body" className="text-sm text-gray-500">
            {t("profileNotificationsDesc")}
          </Text>
        </Card>

        {/* Settings */}
        <Card
          variant="hover"
          padding="md"
          onClick={handleSettingsClick}
          className="cursor-pointer"
        >
          <Box className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
            <Settings className="w-6 h-6 text-gray-600" />
          </Box>
          <Text variant="h5" className="mb-1">
            {t("profileSettings")}
          </Text>
          <Text variant="body" className="text-sm text-gray-500">
            {t("profileSettingsDesc")}
          </Text>
        </Card>
      </Box>

      {/* Verification Notice for Unverified Partners */}
      {userIsPartner &&
        (verificationStatus === "unverified" ||
          verificationStatus === "rejected") && (
          <Card
            variant="default"
            padding="md"
            className="bg-blue-50 border-blue-200 mb-6"
          >
            <Box className="flex items-center justify-between">
              <Box>
                <Text
                  variant="body"
                  className="text-blue-900 font-semibold mb-1"
                >
                  {verificationStatus === "rejected"
                    ? t("verificationRejected")
                    : t("verificationUnverified")}
                </Text>
                <Text variant="body" className="text-blue-700 text-sm">
                  {t("verificationRequiredMessage")}
                </Text>
              </Box>
              <Button
                variant="primary"
                onClick={() => setShowVerificationModal(true)}
                className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
              >
                {t("verificationStartButton")}
              </Button>
            </Box>
          </Card>
        )}

      {/* Coming Soon Notice */}
      <Card
        variant="default"
        padding="md"
        className="bg-amber-50 border-amber-200 mb-6"
      >
        <Text variant="body" className="text-amber-800 text-sm text-center">
          <Text as="span" weight="bold">
            🚧 {t("profileComingSoon")}
          </Text>
          <br />
          {t("profileComingSoonDesc")}
        </Text>
      </Card>

      {/* Developer Tools */}
      {showDevTools && (
        <Card variant="default" padding="md" className="bg-slate-900 shadow-xl">
          <Box className="flex items-center gap-3 mb-4">
            <Box className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
              <Code className="w-5 h-5 text-emerald-400" />
            </Box>
            <Box>
              <Text variant="h5" className="text-white">
                {t("dashboardDevTools")}
              </Text>
              <Text variant="body" className="text-sm text-slate-400">
                {t("dashboardDevToolsDesc")}
              </Text>
            </Box>
          </Box>

          {/* Verification Requests Link */}
          <Box className="mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(ROUTES.VERIFICATION_REQUESTS)}
              className="w-full justify-between px-4 py-3 bg-slate-800 hover:bg-slate-700 text-left"
            >
              <Box className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-purple-400" />
                <Box>
                  <Text
                    variant="body"
                    className="text-sm font-medium text-white"
                  >
                    {t("verificationRequests")}
                  </Text>
                  <Text variant="caption" className="text-xs text-slate-400">
                    Review partner verification requests
                  </Text>
                </Box>
              </Box>
              <ExternalLink className="w-4 h-4 text-slate-400" />
            </Button>
          </Box>

          <Box className="bg-slate-800 rounded-lg p-4 mb-4">
            <Text variant="body" className="text-sm text-slate-300 mb-3">
              {t("dashboardTokenInfo")}{" "}
              <a
                href={env.apiDocsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
              >
                Swagger UI
                <ExternalLink className="w-3 h-3" />
              </a>
            </Text>

            <Box className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleToggleToken}
                className="bg-slate-700 hover:bg-slate-600 text-slate-200"
              >
                {showToken ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-2" />
                    {t("hide")}
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    {t("dashboardShowToken")}
                  </>
                )}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCopyToken}
                disabled={!token}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {t("dashboardCopied")}
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    {t("dashboardCopyToken")}
                  </>
                )}
              </Button>
            </Box>
          </Box>

          {showToken && token && (
            <Box className="bg-slate-800 rounded-lg p-4 overflow-hidden">
              <Text
                variant="body"
                className="text-xs font-mono break-all text-slate-300"
              >
                {token}
              </Text>
            </Box>
          )}

          {showToken && !token && (
            <Text variant="body" className="text-sm text-amber-400">
              {t("dashboardTokenError")}
            </Text>
          )}
        </Card>
      )}

      {/* Verification Modal */}
      <VerificationModal
        open={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onSuccess={() => {
          setVerificationStatus("pending");
          setShowVerificationModal(false);
        }}
      />
    </BasePage>
  );
});
